import React, { useEffect, useState, useRef } from "react";
import Footer from "../../components/Footer";
import BrochureForm from "../../components/EnquiryForm";
import CTAButtons from "../../components/sticky-button";
import Navbar from "../../components/navbar";
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "../studentactivity/StaticPage.css";
import { Helmet } from "react-helmet-async";
import PageHeader from "../../about/PageHeader";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Blogs = () => {
  const [data, setData] = useState({});
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const blogFullPageRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [swiperInstance, setSwiperInstance] = useState(null);

  const getImageSrc = (img) => {
    if (!img) return "/default-image.jpg";
    if (img.startsWith("http") || img.startsWith("data:")) return img;
    return `data:image/jpeg;base64,${img}`;
  };

  useEffect(() => {
    fetch(`${API_URL}/blogs`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then(result => {
        // Group blogs by academic year
        const grouped = {};
        Object.values(result).forEach(blog => {
          const year = blog.academic_year || '2024-2025';
          if (!grouped[year]) grouped[year] = [];
          grouped[year].push(blog);
        });
        setData(grouped);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Handle window resize
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (selectedBlog && blogFullPageRef.current) {
      blogFullPageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest"
      });
      setTimeout(() => {
        window.scrollBy(0, -400);
      }, 300);
    }
  }, [selectedBlog]);

  useEffect(() => {
    if (swiperInstance && prevRef.current && nextRef.current) {
      swiperInstance.params.navigation.prevEl = prevRef.current;
      swiperInstance.params.navigation.nextEl = nextRef.current;
      swiperInstance.navigation.destroy();
      swiperInstance.navigation.init();
      swiperInstance.navigation.update();
    }
  }, [swiperInstance]);

  useEffect(() => {
    if (selectedBlog) {
      window.scrollTo({ top: 100, behavior: "auto" });
    }
  }, [selectedBlog]);

  const handleSelect = (blog) => {
    setSelectedBlog(prev => prev?.id === blog.id ? null : blog);
  };

  const centerMessage = loading ? "Loading blogs..." :
    error ? error :
    Object.keys(data).length === 0 ? "No blogs found. Check back later." : null;

  return (
    <>
      <Helmet>
        <title>Blogs - TIMSCDR</title>
        <meta name="description" content="Read our latest blogs and articles" />
      </Helmet>
      <Navbar setShowForm={setShowForm} />
      {showForm && <BrochureForm />}
      <CTAButtons />
      <PageHeader
              title="Our Blogs"
              crumbs={[
                { label: "Home", href: "/" },
                { label: "Blogs" },
              ]}
            />
      <div className="static-page-container">
        {selectedBlog ? (
          <div className="activity-full-page" ref={blogFullPageRef}>
            <a
              href="#"
              className="back-link"
              onClick={e => {
                e.preventDefault();
                setSelectedBlog(null);
              }}
              aria-label="Back to blogs"
            >
              <span className="back-arrow">←</span>
              <span className="back-text">Back to Blogs</span>
            </a>

            <div className="activity-card-container">
              <h1 className="activity-page-title">{selectedBlog.title}</h1>
              <span className="date-text">
                {(() => {
                  const d = new Date(selectedBlog.selected_date);
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  return `${year}-${month}-${day}`;
                })()}
              </span>
              <div className="activity-content-wrapper">
                {selectedBlog.image && (
                  <div className="activity-hero">
                    <img
                      src={getImageSrc(selectedBlog.image)}
                      alt={selectedBlog.title}
                      className="activity-hero-image"
                      onError={e => e.target.src = "/default-image.jpg"}
                    />
                  </div>
                )}
                <div className="activity-body">
                  <div className="activity-container">
                    <div className="activity-text-content">
                      {selectedBlog.text ? (
                        selectedBlog.text.split("\n").map((paragraph, idx) =>
                          paragraph.trim() && <p key={idx}>{paragraph}</p>
                        )
                      ) : (
                        <p>No content available.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>

            {centerMessage ? (
              <div className="center-message">{centerMessage}</div>
            ) : (
              <div className="content-wrapper">
                <div className="main-content-1">
                  {Object.entries(data).map(([year, blogs]) => (
                    <div key={year} id={`year-${year}`} className="year-section">
                      <h1>A.Y. {year}</h1>
                      <div className="activity-grid">
                        {blogs.map((blog) => (
                          <div
                            key={blog.id}
                            className="activity-card"
                            onClick={() => handleSelect(blog)}
                            style={{ cursor: "pointer" }}
                          >
                            <img
                              src={getImageSrc(blog.image)}
                              alt={blog.title}
                              className="activity-img"
                              onError={e => e.target.src = "/default-image.jpg"}
                            />
                            <div className="activity-content">
                              {blog.selected_date && (
                                <div className="activity-date">
                                  {(() => {
                                    const d = new Date(blog.selected_date);
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    return `${year}-${month}-${day}`;
                                  })()}
                                </div>
                              )}
                              <h3 className="activity-title">{blog.title}</h3>
                              <div className="activity-summary-wrapper">
                                <p className="activity-summary">
                                  {blog.text
                                    ? blog.text.split("\n")[0].slice(0, 120) +
                                      (blog.text.length > 120 ? "..." : "")
                                    : "No description available."}
                                </p>
                              </div>
                              <button className="activity-expand-btn" type="button">
                                <span>Read More &rarr;</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CTAButtons />
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
            >×</button>
            <BrochureForm />
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default Blogs;
